import { Input, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { AxiosError } from 'axios'
import { t } from 'i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  AllowClearIcon,
  EmptyPlaceholder,
  ListPageHeader,
  ListPageLayout,
  PageContentDropdown,
  SubHeader,
  TableBoldColumnContainer,
} from '../../components'
import {
  NO_DATA_PLACEHOLDER,
  permissionActions,
  permissionNames,
} from '../../constants/constants'
import { permissionValues } from '../../constants/permissionConstant'
import { useMediaQuery, useRoles } from '../../hooks'
import useUsers, { UserResponse } from '../../hooks/useUsers'
import { userService } from '../../services'
import { RootState } from '../../store'
import { Role } from '../../store/reducers/roleReducer'
import { User, setUsers } from '../../store/reducers/userReducer'
import { routePaths } from '../RootPage'
import UserListFilters from './UserListFilters'
import CreateUserModal from './modals/CreateUserModal'

const StyledSubHeader = styled(SubHeader)<{ $isMobile: boolean }>`
  ${(props) =>
    props.$isMobile &&
    `
      > :first-child {
        margin-bottom: 8px;
      }
    `}
`

const SearchContainer = styled.div<{ $isMobile: boolean }>`
  flex: 1;
  min-width: 200px;

  ${(props) =>
    props.$isMobile &&
    `
      width: 100%;
    `}
`

const { Search } = Input

interface ExtendedUser extends User {
  key: string
  roleName?: string
}

export type UserListFilterType =
  | 'all'
  | 'active'
  | 'inactive'
  | 'admins'
  | 'users'

export type UserListFilterTypes = {
  ALL_USERS: UserListFilterType
  ACTIVE_USERS: UserListFilterType
  INACTIVE_USERS: UserListFilterType
  ADMIN_ROLE: UserListFilterType
  USER_ROLE: UserListFilterType
}

export const userListFilters: UserListFilterTypes = {
  ALL_USERS: 'all',
  ACTIVE_USERS: 'active',
  INACTIVE_USERS: 'inactive',
  ADMIN_ROLE: 'admins',
  USER_ROLE: 'users',
}

const UserListPage = () => {
  const { filter } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { roleList } = useRoles({})
  const isMobile = useMediaQuery('xs')
  const { exportUserListCsv } = useUsers({ preventFetch: true })
  const userList: User[] | undefined = useSelector(
    (state: RootState) => state.user.users
  )
  const [loading, setLoading] = useState(false)
  const [localSearchValue, setLocalSearchValue] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<UserListFilterType>(
    (filter || userListFilters.ALL_USERS) as UserListFilterType
  )
  const handleFilterChange = (value: UserListFilterType) => {
    setSelectedFilter(value)
    handleSearch('')
    navigate(`${routePaths.USER_LIST}/${value}`)
  }

  // Helper function
  const getFilteredUsers = (
    userList: User[],
    filter: string,
    roleList: Role[]
  ) => {
    switch (filter) {
      case userListFilters.ALL_USERS:
        return userList || []
      case userListFilters.ACTIVE_USERS:
        return userList.filter((user) => user.isActive)
      case userListFilters.INACTIVE_USERS:
        return userList.filter((user) => !user.isActive)
      case userListFilters.ADMIN_ROLE: {
        const adminRole = roleList.find((role) =>
          role.permissions.includes(permissionNames.ALL)
        )
        return userList.filter((user) => user.role === adminRole?.id)
      }
      case userListFilters.USER_ROLE: {
        const userPermission = `${permissionNames.USERS}:${permissionActions.UPDATE}:${permissionValues.OWN}`
        return userList.filter((user) => {
          const userRole = roleList.find((role) =>
            role.permissions.includes(userPermission)
          )
          return userRole && user.role === userRole.id
        })
      }
      default:
        return userList || []
    }
  }

  const formattedData: ExtendedUser[] = useMemo(() => {
    if (!userList) return []

    const filteredUsers = getFilteredUsers(userList, selectedFilter, roleList)

    return filteredUsers.map((user: User) => ({
      ...user,
      key: user.id,
    }))
  }, [userList, selectedFilter, roleList])

  const updateUserList = useCallback(
    (searchQuery?: string) => {
      const service = !!searchQuery
        ? userService.getUsersWithSearchQuery
        : userService.getUsers
      setLoading(true)
      service({ searchQuery: searchQuery || '' })
        .then((response: UserResponse) => {
          setLoading(false)
          dispatch(setUsers(response.data))
        })
        .catch((error: AxiosError | Error) => {
          setLoading(false)
          console.error('axios fetch error', error)
        })
    },
    [dispatch]
  )

  useEffect(() => {
    // Update selected filter when the filter prop changes
    setSelectedFilter(
      (filter || userListFilters.ALL_USERS) as UserListFilterType
    )
  }, [filter])

  useEffect(() => {
    // Fetch and update user list based on the selected filter
    updateUserList(localSearchValue)
  }, [selectedFilter, localSearchValue, updateUserList])

  const handleSearch = (searchText: string) => {
    setLocalSearchValue(searchText)
    updateUserList(searchText)
  }

  const handleClick = (id: string) => {
    navigate(`${routePaths.USER_DETAILS}/${id}`)
  }

  const columns: ColumnsType<User> = [
    {
      title: t('Name'),
      dataIndex: 'displayName',
      key: 'displayName',
      width: '40%',
      sorter: (a: User, b: User) =>
        a.displayName && b.displayName
          ? a.displayName.localeCompare(b.displayName)
          : -1,
      render: (text) => (
        <TableBoldColumnContainer>
          {text || NO_DATA_PLACEHOLDER}
        </TableBoldColumnContainer>
      ),
    },

    {
      title: t('Email'),
      key: 'email',
      dataIndex: 'email',
      width: '30%',
      sorter: (a: User, b: User) =>
        a.email && b.email ? a.email.localeCompare(b.email) : -1,
      render: (text) => <>{text || NO_DATA_PLACEHOLDER}</>,
    },
    {
      title: t('Role'),
      key: 'role',
      dataIndex: 'roleName',
      sorter: (a: User, b: User) =>
        a.role && b.role ? a.role.localeCompare(b.role) : -1,
      render: (text) => <>{text || NO_DATA_PLACEHOLDER}</>,
    },
    {
      title: t('Status'),
      key: 'isActive',
      dataIndex: 'isActive',
      sorter: (a: User, b: User) => (a.isActive && b.isActive ? 1 : -1),
      render: (text) => <>{text ? t('Active') : t('Inactive')}</>,
    },
  ]

  const handleUserListExport = () => {
    exportUserListCsv()
  }

  return (
    <ListPageLayout>
      <ListPageHeader
        title={t('userListPage.title', {
          count: userList?.length,
        })}
        actions={
          <>
            <CreateUserModal />
            <PageContentDropdown
              onExport={handleUserListExport}
              disabled={formattedData.length === 0}
            />
          </>
        }
      />
      <StyledSubHeader $isMobile={isMobile}>
        <UserListFilters onChange={handleFilterChange} />
        <SearchContainer $isMobile={isMobile}>
          <Search
            placeholder={t('userListPage.searchPlaceholder') || ''}
            allowClear={{
              clearIcon: <AllowClearIcon />,
            }}
            onSearch={handleSearch}
            onChange={(event) => handleSearch(event.target.value)}
            value={localSearchValue}
          />
        </SearchContainer>
      </StyledSubHeader>
      <Table
        loading={loading}
        rowClassName="page-list-table-row clickable"
        columns={columns}
        dataSource={formattedData}
        pagination={false}
        scroll={{
          x: isMobile ? 'max-content' : '100%',
          y: 'calc(100vh - 202px)',
        }}
        locale={{
          emptyText: (
            <EmptyPlaceholder description={t('userListPage.noUsers')} />
          ),
        }}
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {
              handleClick(record.id)
            },
          }
        }}
      />
    </ListPageLayout>
  )
}

export default UserListPage
